import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as authApi from '../services/authApi';
import apiClient, { setAccessToken, setUnauthorizedHandler, clearViewAs } from '../services/apiClient';
import ViewAsControl from '../components/ViewAsControl';
import MyReportSwitch from '../components/MyReportSwitch';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // 'checking' | 'authenticated' | 'unauthenticated'
  const [status, setStatus] = useState('checking');

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(clearSession);
  }, [clearSession]);

  // On first load, there's no user in memory yet (SPA reload) — try the httpOnly
  // refresh cookie once to silently restore the session.
  useEffect(() => {
    authApi
      .refreshSession()
      .then(async ({ accessToken }) => {
        setAccessToken(accessToken);
        const profile = await authApi.getMe();
        setUser(profile);
        setStatus('authenticated');
      })
      .catch(() => {
        setStatus('unauthenticated');
      });
  }, []);

  // Heartbeat: while the person is really working (tab visible, used within the last
  // 5 minutes) tell the server once a minute, so Login Activity counts only active time.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    let lastInput = Date.now();
    const mark = () => { lastInput = Date.now(); };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible' && Date.now() - lastInput < 5 * 60 * 1000) {
        apiClient.post('/activity/ping').catch(() => {});
      }
    }, 60 * 1000);
    return () => {
      clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, mark));
    };
  }, [status]);

  const login = useCallback(async (email, password) => {
    clearViewAs();
    const { accessToken, user: loggedInUser } = await authApi.login(email, password);
    setAccessToken(accessToken);
    setUser(loggedInUser);
    setStatus('authenticated');
    return loggedInUser;
  }, []);

  const logout = useCallback(async () => {
    clearViewAs();
    try {
      await authApi.logout();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  return (
    <AuthContext.Provider value={{ user, status, login, logout }}>
      {children}
      <ViewAsControl user={user} />
      <MyReportSwitch user={user} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
